import * as React from 'react'
import { useSelector } from 'react-redux'
import {
  Autocomplete,
  Box,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography as MIUTypography,
  styled,
} from '@mui/material'
import { DataGrid, gridClasses, esES } from '@mui/x-data-grid'
import { getPseudonymTableAll } from '../../../services'
import { grey } from '@mui/material/colors'
import { getColumns } from './columns'
import Botton from '../../BottonCustom'
import { Create } from '@mui/icons-material'
import CreationModal from './CreationModal'
import DeletePseudonymModal from './DeletePseudonymModal'
import { usePersistedState } from '../../../hooks/usePersistedState'
import AlertCustom from '../../Alerts'

const styleContainer = { height: '100%', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }

const styleDefault = theme => ({
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.primary.main}`,
  width: '100%',
  fontSize: '12px',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  '&:active': { boxShadow: 'none', opacity: 0.9 },
})

const styleHeaderTab = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignContent: 'center',
  alignItems: 'baseline',
  ml: 4,
  mb: 1,
}

const Typography = styled(MIUTypography)(props => ({
  fontFamily: props.theme.typography.fontFamily,
  fontStyle: 'normal',
  fontSize: '1em',
  fontWeight: 650,
  lineHeight: '18px',
  color: '#6A6A6A',
  paddingBottom: 6,
  paddingTop: 6,
  textAlign: 'center',
  marginTop: 12,
  marginBottom: 3,
  height: '5%',
}))

const buildSpeciesFilterOptions = rows => {
  const uniqueBySpecieId = new Map()

  rows.forEach(row => {
    if (row?.idEspecie === undefined || row?.idEspecie === null) return
    const key = String(row.idEspecie)
    if (!uniqueBySpecieId.has(key)) {
      uniqueBySpecieId.set(key, {
        id: row.idEspecie,
        label: row.nombre_cientifico || `Especie #${row.idEspecie}`,
      })
    }
  })

  return Array.from(uniqueBySpecieId.values()).sort((a, b) =>
    a.label.localeCompare(b.label, 'es', { sensitivity: 'accent' })
  )
}

const filterRows = (rows, commonNameSearch, selectedSpecieIds) => {
  const commonQuery = commonNameSearch.trim().toLowerCase()
  const selectedSet = new Set(selectedSpecieIds)

  return rows.filter(row => {
    const commonName = (row?.nombre_comun || '').toLowerCase()
    const commonMatches = !commonQuery || commonName.includes(commonQuery)
    const specieMatches = selectedSet.size === 0 || selectedSet.has(String(row.idEspecie))

    return commonMatches && specieMatches
  })
}

const PseudonymTable = () => {
  const modalRef = React.useRef()
  const alertRef = React.useRef()
  const deleteModalRef = React.useRef()

  const handleRequestDelete = React.useCallback(pseudonym => {
    deleteModalRef.current?.openModal(pseudonym)
  }, [])

  const [data, setData] = React.useState([])
  const [commonNameSearch, setCommonNameSearch] = usePersistedState('admin.pseudonym.commonNameSearch', '')
  const [selectedSpecieIds, setSelectedSpecieIds] = usePersistedState('admin.pseudonym.selectedSpecieIds', [])
  const [expandedRowIds, setExpandedRowIds] = React.useState([])
  const [pageSize, setPageSize] = usePersistedState('admin.pseudonym.pageSize', 20)
  const [rowId, setRowId] = React.useState(null)
  const [loading, setLoading] = React.useState(false)

  const { isStored } = useSelector(state => state.alertsGlobal)

  const onToggleRow = React.useCallback(rowIdToToggle => {
    setExpandedRowIds(prev =>
      prev.includes(rowIdToToggle)
        ? prev.filter(id => id !== rowIdToToggle)
        : [...prev, rowIdToToggle]
    )
  }, [])

  const columns = React.useMemo(
    () => getColumns(rowId, setRowId, expandedRowIds, onToggleRow, handleRequestDelete),
    [rowId, expandedRowIds, onToggleRow, handleRequestDelete]
  )

  const specieFilterOptions = React.useMemo(() => buildSpeciesFilterOptions(data), [data])

  const selectedSpecieOptions = React.useMemo(
    () => specieFilterOptions.filter(option => selectedSpecieIds.includes(String(option.id))),
    [specieFilterOptions, selectedSpecieIds]
  )

  const filteredBaseRows = React.useMemo(
    () => filterRows(data, commonNameSearch, selectedSpecieIds),
    [data, commonNameSearch, selectedSpecieIds]
  )

  const rows = React.useMemo(() => {
    const expanded = new Set(expandedRowIds)
    const mappedRows = []

    filteredBaseRows.forEach(pseudonym => {
      mappedRows.push(pseudonym)
      if (expanded.has(pseudonym.id)) {
        mappedRows.push({
          ...pseudonym,
          id: `detail-${pseudonym.id}`,
          __isDetail: true,
          __parentId: pseudonym.id,
        })
      }
    })

    return mappedRows
  }, [filteredBaseRows, expandedRowIds])

  React.useEffect(() => {
    const visibleIds = new Set(filteredBaseRows.map(row => row.id))
    setExpandedRowIds(prev => prev.filter(id => visibleIds.has(id)))
  }, [filteredBaseRows])

  const loadData = React.useCallback(async () => {
    setLoading(true)
    try {
      const rows = await getPseudonymTableAll()
      setData(rows || [])
    } catch (err) {
      console.log('Error: ', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleFormCreation = () => {
    modalRef.current.openModal()
  }

  React.useEffect(() => {
    loadData()
  }, [loadData])

  React.useEffect(() => {
    if (!Boolean(isStored)) return
    loadData()
      .then(() => {
        alertRef?.current?.openAlert(true)
      })
      .catch(err => console.log('Error: ', err))
  }, [isStored, loadData])

  return (
    <Box sx={styleContainer}>
      <Box sx={styleHeaderTab}>
        <Box>
          <Typography>Administrar Pseudónimos</Typography>
        </Box>
        <Box>
          <Botton
            text="Nuevo"
            onClick={handleFormCreation}
            startIcon={<Create />}
            styleButton={styleDefault}
            size="small"
          />
        </Box>
      </Box>

      <Paper elevation={1} sx={{ mx: 4, mb: 1.5, p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            fullWidth
            size="small"
            label="Buscar por nombre común"
            placeholder="Ej: acacia amarilla"
            value={commonNameSearch}
            onChange={event => setCommonNameSearch(event.target.value)}
          />

          <Autocomplete
            multiple
            fullWidth
            size="small"
            options={specieFilterOptions}
            value={selectedSpecieOptions}
            onChange={(_, newValue) =>
              setSelectedSpecieIds(newValue.map(item => String(item.id)))
            }
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.id} label={option.label} size="small" />
              ))
            }
            renderInput={params => <TextField {...params} label="Filtrar por especie" />}
          />
        </Stack>
      </Paper>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', px: 2, display: 'flex' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          getRowId={row => row.id}
          rowsPerPageOptions={[20, 50, 100]}
          onPageSizeChange={newPageSize => setPageSize(newPageSize)}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          isCellEditable={params => !params.row?.__isDetail && Boolean(params.colDef.editable)}
          getRowHeight={params => (params.model?.__isDetail ? 'auto' : null)}
          getEstimatedRowHeight={() => 120}
          getRowClassName={params => (params.row?.__isDetail ? 'pseudonym-detail-row' : '')}
          getRowSpacing={params => ({
            top: params.model?.__isDetail ? 0 : params.isFirstVisible ? 0 : 5,
            bottom: params.model?.__isDetail ? 8 : 0,
          })}
          sx={{
            flex: 1,
            [`& .${gridClasses.row}`]: {
              bgcolor: grey[100],
            },
            '& .pseudonym-detail-row': {
              bgcolor: '#F9FAFC !important',
            },
            '& .pseudonym-detail-cell': {
              py: '0 !important',
              alignItems: 'flex-start',
            },
            '& .pseudonym-expand-cell': {
              pl: 0.5,
            },
          }}
          loading={loading}
          onCellEditCommit={params => {
            if (typeof params.id === 'string' && params.id.startsWith('detail-')) {
              return
            }
            setRowId(params.id)
          }}
        />
      </Box>

      <CreationModal ref={modalRef} />
      <DeletePseudonymModal ref={deleteModalRef} />
      <AlertCustom
        ref={alertRef}
        severity={isStored?.stored !== true ? 'warning' : 'success'}>
        {isStored?.stored !== true ? isStored?.message : 'Guardado exitosamente.'}
      </AlertCustom>
    </Box>
  )
}

export default PseudonymTable
